import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Divider
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";

import api, { API_BASE_URL } from "../../utils/api";

import MemberCard from "./Details/MemberCard";
import StatusChip from "./Details/StatusChip";
import RequestDetailsPanel from "./Details/RequestDetailsPanel";

import Calendar from "../Calendar";
import ArchitecturalRequestComments from "./ArchitecturalRequestComments";
import ArchitecturalRequestVisits from "./ArchitecturalRequestVisits";

const ArchitecturalRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem("currentUserId") || 1;

  const [requestDetails, setRequestDetails] = useState(null);
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const requestResp = await api.get(`/architectural-requests/${id}`);
        const requestData = requestResp.data;

        const memberResp = await api.get(`/members/${requestData.property_id}`);
        const memberData = memberResp.data;

        const priorityResp = await api.get(`/priority-levels`);
        const priorities = priorityResp.data;

        // Match the priority color
        const matchedPriority = priorities.find(
          (p) => p.priority_value === requestData.priority
        );
        requestData.priority_color = matchedPriority?.color_code || "#1976d2";

        setRequestDetails(requestData);
        setMember(memberData);
      } catch (error) {
        console.error("Failed to fetch details:", error);
        setError("Failed to fetch details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const priorityColor = requestDetails?.priority_color || "#1976d2";

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center", marginTop: 4 }}>
        {error}
      </Typography>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ padding: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/home/architecturalrequest", { state: { reload: true } })}
          sx={{ mb: 3 }}
        >
          Back
        </Button>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <MemberCard member={member} id={id} />
          </Grid>

          <Grid item xs={12} md={8}>
            <Card
              sx={{
                p: 3,
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                border: `2px solid ${priorityColor}`
              }}
            >
              <CardContent>
                {/* Status Chip */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <StatusChip requestedAt={requestDetails?.requested_at} />
                </Box>

                <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 3 }}>
                  Architectural Request Details
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* Tab Navigation */}
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  textColor="primary"
                  indicatorColor="primary"
                  variant="fullWidth"
                  sx={{ marginBottom: 3 }}
                >
                  <Tab label="Details" />
                  <Tab label="Calendar" />
                  <Tab label="Comments" />
                  <Tab label="Visits" />
                </Tabs>

                {/* Tab Panels */}
                {activeTab === 0 && requestDetails && (
                  <RequestDetailsPanel
                    requestDetails={requestDetails}
                    onUpdate={(updatedData) =>
                      setRequestDetails((prevDetails) => ({
                        ...prevDetails,
                        ...updatedData,
                      }))
                    }
                  />
                )}

{activeTab === 1 && (
  <Calendar
    architectural_request_id={id}
    refreshTrigger={activeTab}
  />
)}
{activeTab === 2 && (
  <ArchitecturalRequestComments
    architectural_request_id={id}
    currentUserId={currentUserId}
    refreshTrigger={activeTab}
  />
)}
{activeTab === 3 && (
  <ArchitecturalRequestVisits
    architectural_request_id={id}
    currentUserId={currentUserId}
    refreshTrigger={activeTab}
  />
)}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ArchitecturalRequestDetails;
